import type { ProjectionWithRelations } from '@/lib/supabase'

/**
 * Resolve a projection's signed amount and flow direction.
 *
 * Phase-1 of DCP-250 dropped the old `credit_amount`/`debit_amount` split
 * columns and replaced them with a single signed `amount` column:
 *   positive value → inflow  → direction = 'credit'
 *   negative value → outflow → direction = 'debit'
 *
 * The `amount` returned here is the SIGNED stored value (NOT absolute).
 * Phase-3 call sites that previously received an absolute amount and relied on
 * `direction` to determine sign must be updated to render the sign from the
 * value itself (see AC#3/#4 in DCP-250).
 *
 * Zero is treated as 'credit' to provide a stable default; this is a cosmetic
 * choice — a zero-amount projection has no real direction.
 *
 * The old category.type fallback branch is removed: after the Phase-1
 * backfill the amount column is authoritative and `categories.type` is
 * nullable, so it is no longer a reliable fallback.
 *
 * @param projection - The projection record (must include the `amount` field)
 * @returns `{ amount, direction }` where `amount` is the signed numeric value
 *          and `direction` is `'credit'` for non-negative or `'debit'` for negative.
 */
export function getProjectionAmountInfo(projection: ProjectionWithRelations): {
  amount: number
  direction: 'credit' | 'debit'
} {
  const { amount } = projection
  // Negative amount = outflow = debit; zero defaults to credit (no real direction).
  const direction: 'credit' | 'debit' = amount < 0 ? 'debit' : 'credit'
  return { amount, direction }
}

/**
 * Resolve the default priority value for a given category.
 * Returns `default_priority` of the matched category, or `false` if not found.
 */
export function getDefaultPriority(
  categories: { id: string; default_priority?: boolean }[],
  categoryId: string
): boolean {
  const category = categories.find((c) => c.id === categoryId)
  return category?.default_priority ?? false
}
